/**
 * The exercise catalog, as the model sees it: names, props (Zod) and what each component does.
 * This file must stay DOM-free: `scripts/gen-catalog.mts` imports it under Node to generate the
 * catalog text in `server/src/catalog.generated.ts`, so the prompt can never drift from the schemas.
 * Component implementations live in `./components/`.
 */
import { z } from 'zod';
import { CommonSchemas } from '@a2ui/web_core/v0_9';

export const CATALOG_ID = 'https://learnimmerse.app/a2ui/exercises-v1.json';
export const SURFACE_ID = 'lesson';

export interface ExerciseApi<S extends z.ZodTypeAny = z.ZodTypeAny> {
  name: string;
  schema: S;
  /** One line for the model: what it is and when to use it. */
  description: string;
  /** What the learner's interaction sends back, if anything. */
  action?: string;
}

const def = <S extends z.ZodTypeAny>(a: ExerciseApi<S>) => a;

export const ColumnApi = def({
  name: 'Column',
  schema: z.object({ children: CommonSchemas.ChildList.describe('ids of the children, top to bottom') }),
  description: 'vertical stack; use as root',
});

export const ExplainApi = def({
  name: 'Explain',
  schema: z.object({
    title: z.string().optional(),
    text: z.string().describe('the explanation in English'),
    ca: z.string().optional().describe('a Catalan line shown big above the text'),
  }),
  description: 'a short explanation card',
});

export const ProgressApi = def({
  name: 'Progress',
  schema: z.object({
    done: z.number(),
    total: z.number(),
    label: z.string().optional(),
  }),
  description: 'small progress indicator',
});

export const FlashcardApi = def({
  name: 'Flashcard',
  schema: z.object({
    ca: z.string(),
    en: z.string(),
    note: z.string().optional(),
    example: z.string().optional(),
    latin: z.string().optional().describe('Latin transliteration shown big when the learner cannot read the script (Persian Finglish)'),
    exampleLatin: z.string().optional().describe('Latin transliteration of the example, shown first (Persian Finglish)'),
  }),
  description: 'front shows Catalan, tap to flip to English + note + example, then self-grade with the Missed / Got-it buttons',
  action: 'sends "flipped" {ca} when turned over, then "grade" {ca, correct, expected} from the self-grade buttons',
});

export const GapFillApi = def({
  name: 'GapFill',
  schema: z.object({
    sentence: z.string().describe('contains exactly one "___"'),
    answer: z.string(),
    accept: z.array(z.string()).optional().describe('alternative answers that also count as correct'),
    hint: z.string().optional(),
    translation: z.string().optional(),
    spelling: z.array(z.string()).optional().describe('set by the app only, never by the model: a spelling card, naming the spikes to mind'),
  }),
  description: 'learner types the missing word or phrase (recall tasks accept listed alternatives)',
  action: 'sends "answer" {value, correct, expected, revealed?, scratched?, missed?}, "hint" {tier} per hint revealed, "dispute" {value, expected} when the learner says their wrong answer should count',
});

export const ChoiceApi = def({
  name: 'Choice',
  schema: z.object({
    question: z.string(),
    options: z.array(z.string()),
    correct: z.number().describe('index into options'),
    explain: z.string().optional(),
  }),
  description: 'multiple choice',
  action: 'sends "answer" {value, correct, expected}',
});

export const ReorderApi = def({
  name: 'Reorder',
  schema: z.object({
    words: z.array(z.string()).describe('the correct order; the screen shuffles them'),
    translation: z.string().optional(),
  }),
  description: 'learner taps the words back into order',
  action: 'sends "answer" {value, correct, expected, revealed?}',
});

export const SayItBackApi = def({
  name: 'SayItBack',
  schema: z.object({
    ca: z.string(),
    en: z.string(),
    tip: z.string().optional(),
    latin: z.string().optional().describe('Latin transliteration shown big when the learner cannot read the script (Persian Finglish)'),
  }),
  description: 'asks the learner to say the Catalan line out loud; you listen and judge',
  action: 'sends "ready" when they tap "I said it"',
});

export const SpotItApi = def({
  name: 'SpotIt',
  schema: z.object({
    word: z.string().describe('the word to find, as the learner reads it'),
    words: z.array(z.string()).describe('the line, word by word'),
    at: z.array(z.number()).describe("indices into words of the word's own words"),
    question: z.string().describe('asks its meaning once the word is found'),
    options: z.array(z.string()),
    correct: z.number().describe('index into options'),
    explain: z.string().optional(),
    translation: z.string().optional().describe("the line's meaning, shown after answering"),
  }),
  description: 'learner taps the word in its line, then picks its meaning; a wrong tap is a miss',
  action: 'sends "answer" {value, correct, expected} once: on a wrong tap, else on the meaning picked',
});

export const DictationApi = def({
  name: 'Dictation',
  schema: z.object({
    audioText: z.string().describe('the word or phrase the learner hears (the lesson plays it); never shown before answering'),
    answer: z.string(),
    accept: z.array(z.string()).optional().describe('alternative answers that also count as correct'),
    hint: z.string().optional(),
    translation: z.string().optional().describe('its meaning, shown after answering'),
  }),
  description: 'learner hears a word at natural speed and types it',
  action: 'sends "answer" {value, correct, expected, revealed?, scratched?, missed?}, "hint" {tier} per hint revealed, "dispute" {value, expected} when the learner says their wrong answer should count',
});

export const SoundAlikeApi = def({
  name: 'SoundAlike',
  schema: z.object({
    audioText: z.string().describe('the word the learner hears (the lesson plays it); never shown before answering'),
    options: z.array(z.string()).describe('the word and a word one sound away'),
    correct: z.number().describe('index into options'),
    explain: z.string().optional().describe('the word and its meaning, shown after answering'),
  }),
  description: 'learner hears a word and picks it from two words that sound alike',
  action: 'sends "answer" {value, correct, expected}',
});

export const SayFromMeaningApi = def({
  name: 'SayFromMeaning',
  schema: z.object({
    ca: z.string().describe('the word to say; hidden until the learner has said it'),
    en: z.string().describe('its meaning, shown big'),
    latin: z.string().optional().describe('Latin transliteration shown big once revealed, when the learner cannot read the script (Persian Finglish)'),
  }),
  description: 'learner sees a meaning and says the word out loud; you listen and judge',
  action: 'sends "ready" when they tap "I said it"',
});

/** Order here is the order in the prompt. */
export const ALL_APIS: ExerciseApi[] = [
  ColumnApi, ExplainApi, ProgressApi, FlashcardApi, GapFillApi, ChoiceApi, ReorderApi, SayItBackApi, SpotItApi, DictationApi,
  SoundAlikeApi, SayFromMeaningApi,
];
